import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
// Import Types
import type { ProcessTemplateWithAiTemplate } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { changeTemplateValue } from 'store/slices/template';
import { selectPageValidation } from 'store/slices/pageValidation';
// Import UI Components
import { ExtendedInput } from '@geneui/components';
// Import Components
import AIAssistantBanner from './AIAssistantBanner';
// Import Helpers
import { calculateTemplateBodyLengthWithRegEx } from 'services/Helpers';
// Import Services
import { TemplateHttpService } from 'services/http';
import { getTemplateValidationTranslatableErrorText } from 'services/template';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useAutocompleteRequest from 'hooks/UseAutocompleteRequest';
import { useValidator } from 'hooks'; //TODO: change to direct import later
// Import Enums
import { DeliveryMethodEnum, TemplateTypeEnum } from 'enums';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import 'assets/scss/socialNotification.scss';

const SMSContent = (props) => {
    const {
        bodyPropertyName,
        templateData,
        templateLangKey,
        maxSMSPartsCount,
        handleChange,
        shortenerAndTrackerSettings,
        updateDynamicVariableOptions,
        updateInsertionHandler,
        shouldFocusOnMount,
        isAiAssistantEnabled,
        eventId,
    } = props;
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { getAutocompleteRequest, cancelRequest } = useAutocompleteRequest();
    const dispatch = useAppDispatch();

    const templateBody = templateData?.[bodyPropertyName];

    const pageValidation = useAppSelector(selectPageValidation);
    const pageValidationForLanguage = pageValidation[templateLangKey + bodyPropertyName];
    const validator = useValidator(getTemplateValidationTranslatableErrorText(t));

    const [bodyValue, setBodyValue] = useState(templateBody);
    const [moveCursorToPosition, setMoveCursorToPosition] = useState<number | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const { getSMSPartsCount } = useMemo(
        () => ({
            getSMSPartsCount: TemplateHttpService.getSMSPartsCount,
        }),
        [],
    );

    const computedBody = useMemo(() => {
        if (isEmpty(shortenerAndTrackerSettings) || isEmpty(templateBody)) return '';
        return calculateTemplateBodyLengthWithRegEx(shortenerAndTrackerSettings, templateBody);
    }, [templateBody, shortenerAndTrackerSettings]);

    const bodyValidationState = useMemo(() => {
        let isValid = isNil(pageValidationForLanguage?.isValid) || pageValidationForLanguage?.isValid;
        let errorText;

        if (!isValid) {
            errorText = t(pageValidationForLanguage?.errorText, {
                fieldName: l.Body,
                count: pageValidationForLanguage?.paramValue,
            });
        }

        const isValidSMSParts =
            isNil(pageValidation[templateLangKey + 'SMSParts']?.isValid) ||
            pageValidation[templateLangKey + 'SMSParts']?.isValid;

        if (!isValidSMSParts) {
            if (isValid) {
                errorText = t(pageValidation[templateLangKey + 'SMSParts']?.errorText);
            }
            isValid = false;
        }

        return {
            isValid,
            errorText,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageValidation, templateLangKey]);

    const onChange = (e) => {
        const value = e.target.value;

        setBodyValue(value);
        handleChange(bodyPropertyName, value);
    };

    const onFocusHandler = () => {
        updateDynamicVariableOptions();
    };

    const insertTextAtCursor = (dynamicVariable) => {
        const textToInsert = dynamicVariable.FieldName;
        const selectionStart = inputRef.current?.selectionStart;
        const firstPart = bodyValue.slice(0, selectionStart);
        const secondPart = bodyValue.slice(selectionStart, bodyValue.length);
        const insertedText = ` {${textToInsert}} `;
        const resultValue = `${firstPart}${insertedText}${secondPart}`;

        setBodyValue(resultValue);
        setMoveCursorToPosition(firstPart.length + insertedText.length);
        handleChange(bodyPropertyName, resultValue);
    };

    updateInsertionHandler(bodyPropertyName, insertTextAtCursor);

    // The textarea is driven by a local mirror of the stored body, so an applied template has to
    // land in both. The `0` timeout skips the shared debounce, so validation and the SMS parts
    // recalculation run against the new text right away.
    const handleApplyAiTemplate = useCallback(
        ({ Body }: ProcessTemplateWithAiTemplate) => {
            setBodyValue(Body);
            handleChange(bodyPropertyName, Body, 0);
        },
        [bodyPropertyName, handleChange],
    );

    // The caret is placed after the textarea receives the new value; setting it earlier is lost
    // because writing the controlled value moves the caret to the end.
    useEffect(() => {
        if (!isNil(moveCursorToPosition)) {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(moveCursorToPosition, moveCursorToPosition);
            setMoveCursorToPosition(null);
        }
    }, [moveCursorToPosition]);

    useEffect(() => {
        validator({ [templateLangKey + 'SMSParts']: templateData.SMSParts });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateData.SMSParts]);

    useEffect(() => {
        if (isEmpty(computedBody)) {
            dispatch(changeTemplateValue({ propertyName: 'SMSParts', value: 1, templateLangKey }));
        } else {
            doGetRequest(getAutocompleteRequest(getSMSPartsCount).request, {
                successCallback: (data) =>
                    dispatch(changeTemplateValue({ propertyName: 'SMSParts', value: data, templateLangKey })),
                queryString: { message: computedBody },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedBody]);

    useEffect(() => {
        if (shouldFocusOnMount) {
            onFocusHandler();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cleanUp = () => () => cancelRequest('SMSTemplateContent:getSMSPartsCount');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <div className="crm-create-template-page-c-c-content">
            <AIAssistantBanner
                isEnabled={isAiAssistantEnabled}
                deliveryMethod={DeliveryMethodEnum.SMS}
                subject={null}
                body={bodyValue ?? ''}
                language={templateLangKey}
                eventId={eventId}
                onApply={handleApplyAiTemplate}
                isPlainText={true}
            />
            <div className="crm-messaging-row">
                <div className="crm-messaging-row-head">
                    <p>{t(l.CharactersCountIn, { pageName: TemplateTypeEnum.SMS })}</p>
                    <span>{computedBody.length}</span>
                    <p>{t(l.SMSParts)}:</p>
                    <span>{`${isNil(templateData.SMSParts) ? 1 : templateData.SMSParts} / ${maxSMSPartsCount}`}</span>
                </div>

                <div className={`crm-messaging-row-content ${bodyValidationState.isValid ? '' : 'error-color'}`}>
                    <ExtendedInput
                        ref={inputRef}
                        type="textarea"
                        value={bodyValue}
                        colorBorderOnError={true}
                        isValid={bodyValidationState.isValid}
                        errorText={bodyValidationState.errorText}
                        placeholder={t(l.TypeYourText)}
                        onChange={onChange}
                        onFocus={onFocusHandler}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={shouldFocusOnMount}
                    />
                </div>
            </div>
        </div>
    );
};

SMSContent.propTypes = {
    bodyPropertyName: PropTypes.string.isRequired,
    templateData: PropTypes.object.isRequired,
    templateLangKey: PropTypes.string.isRequired,
    maxSMSPartsCount: PropTypes.number.isRequired,
    handleChange: PropTypes.func.isRequired,
    updateFocusedInput: PropTypes.func,
    shortenerAndTrackerSettings: PropTypes.array.isRequired,
    updateDynamicVariableOptions: PropTypes.func.isRequired,
    updateInsertionHandler: PropTypes.func.isRequired,
    shouldFocusOnMount: PropTypes.bool.isRequired,
    isAiAssistantEnabled: PropTypes.bool,
    eventId: PropTypes.number,
};

SMSContent.defaultProps = {
    shouldFocusOnMount: true,
    // The Viber and WhatsApp Provider cascades reuse this content for their SMS leg and
    // must not offer the assistant, so it stays opt-in.
    isAiAssistantEnabled: false,
};

export default SMSContent;
